/**
 * Which WireGuard tunnels carry a site (ENG-2056).
 *
 * Admin stores no site-to-tunnel link. A site's `Customer.wireguard` is its
 * WireGuard GATEWAY (availability zone), not a tunnel. The connector runs on
 * a node in that zone, whose route manager routes every one of the zone's
 * tunnels by destination subnet, so the tunnel carrying an address is the one
 * whose routed range contains it, whatever group owns it. WireGuard subnets
 * are unique among WireGuard tunnels, so each address matches at most one.
 *
 * Ported from the redesign branch's Troubleshooter (`siteTunnel.ts`) so the
 * Site About page can share the rule without depending on redesign-only code.
 */

/** The fields tunnel resolution reads; both tunnel interfaces satisfy it. */
export interface RoutableTunnel {
  id: number;
  server: number | null;
  subnets: string[] | null;
  client_tunnel_ip: string | null;
}

export const ipv4ToNumber = (value: string): number | null => {
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(
    value.trim()
  );
  if (!match) return null;
  const parts = match.slice(1).map(Number);
  if (parts.some((part) => part > 255)) return null;
  return (
    ((parts[0] << 24) >>> 0) + (parts[1] << 16) + (parts[2] << 8) + parts[3]
  );
};

export const cidrContains = (cidr: string, address: string): boolean => {
  const [base, bitsText] = cidr.trim().split("/");
  const baseValue = ipv4ToNumber(base);
  const target = ipv4ToNumber(address);
  if (baseValue == null || target == null) return false;
  const bits = bitsText == null ? 32 : Number(bitsText);
  if (!Number.isInteger(bits) || bits < 0 || bits > 32) return false;
  const mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0;
  return (baseValue & mask) >>> 0 === (target & mask) >>> 0;
};

/**
 * The ranges a tunnel carries, as admin's `scan_targets_for_tunnel` judges
 * them: its subnets, or, when it has none, its own client tunnel address
 * (the single-box NVR case: WireGuard runs on the recorder itself).
 */
export const tunnelRoutedRanges = (tunnel: RoutableTunnel): string[] => {
  const subnets = (tunnel.subnets ?? []).filter(
    (cidr) => typeof cidr === "string" && cidr.trim()
  );
  if (subnets.length) return subnets;
  const own = (tunnel.client_tunnel_ip ?? "").trim();
  return own ? [own] : [];
};

/** An address the connector dials, with what it is (camera name, "Server"). */
export interface SiteHost {
  address: string;
  label: string;
}

export interface SiteHostSource {
  server_ip?: string | null;
  management_server_ip?: string | null;
  event_server_ip?: string | null;
}

/**
 * Every address the site's connector dials: its server / management / event
 * server and each camera's saved address. Deduplicated by address; the first
 * label wins, and further names for the same address are appended.
 */
export const siteHosts = (
  site: SiteHostSource | null | undefined,
  cameras: { camera_name?: string | null; ip?: string | null }[] = []
): SiteHost[] => {
  const entries: SiteHost[] = [
    { address: site?.server_ip ?? "", label: "Server" },
    { address: site?.management_server_ip ?? "", label: "Management server" },
    { address: site?.event_server_ip ?? "", label: "Event server" },
    ...cameras.map((camera) => ({
      address: camera.ip ?? "",
      label: camera.camera_name || "Camera",
    })),
  ];
  const byAddress = new Map<string, SiteHost>();
  entries.forEach(({ address, label }) => {
    const trimmed = String(address ?? "").trim();
    if (!trimmed) return;
    const existing = byAddress.get(trimmed);
    if (!existing) {
      byAddress.set(trimmed, { address: trimmed, label });
    } else if (!existing.label.split(", ").includes(label)) {
      existing.label = `${existing.label}, ${label}`;
    }
  });
  return Array.from(byAddress.values());
};

export interface SiteTunnelUse<T extends RoutableTunnel> {
  tunnel: T;
  hosts: SiteHost[];
  /**
   * The tunnel sits on a different gateway zone from the site's connector,
   * so the connector's node never programs its routes: a misconfiguration.
   */
  otherZone: boolean;
}

export interface SiteTunnelUsage<T extends RoutableTunnel> {
  used: SiteTunnelUse<T>[];
  /** IPv4 addresses no tunnel routes: they go over the VPC routes instead. */
  notOnWireGuard: SiteHost[];
  /** Hostnames, which can't be matched to a subnet without resolving them. */
  hostnames: SiteHost[];
}

/**
 * Group the site's addresses by the tunnel that routes them.
 *
 * `sameZone(tunnelServer)` says whether a tunnel's gateway is in the site's
 * connector zone; callers resolve zones from the gateway list.
 */
export const resolveSiteTunnelUsage = <T extends RoutableTunnel>(
  hosts: SiteHost[],
  tunnels: T[],
  sameZone: (tunnelServer: number | null) => boolean
): SiteTunnelUsage<T> => {
  const used = new Map<number, SiteTunnelUse<T>>();
  const notOnWireGuard: SiteHost[] = [];
  const hostnames: SiteHost[] = [];
  hosts.forEach((host) => {
    if (ipv4ToNumber(host.address) == null) {
      hostnames.push(host);
      return;
    }
    const tunnel = tunnels.find((t) =>
      tunnelRoutedRanges(t).some((cidr) => cidrContains(cidr, host.address))
    );
    if (!tunnel) {
      notOnWireGuard.push(host);
      return;
    }
    const entry = used.get(tunnel.id);
    if (entry) {
      entry.hosts.push(host);
    } else {
      used.set(tunnel.id, {
        tunnel,
        hosts: [host],
        otherZone: !sameZone(tunnel.server),
      });
    }
  });
  return { used: Array.from(used.values()), notOnWireGuard, hostnames };
};

/**
 * Whether two gateways share a zone: by the gateways' `zone` when both are
 * known, otherwise only the same gateway counts.
 */
export const gatewaysShareZone = (
  a: number | null | undefined,
  b: number | null | undefined,
  gateways: { id: number; zone?: string | null }[]
): boolean => {
  if (a == null || b == null) return false;
  if (a === b) return true;
  const zoneA = gateways.find((g) => g.id === a)?.zone;
  const zoneB = gateways.find((g) => g.id === b)?.zone;
  return Boolean(zoneA && zoneB && zoneA === zoneB);
};
